// ============================================================================
// GuestLedgerReportScreen — Posted vs Billed (PMS Phase 2 slice 6c)
// ============================================================================
// The guest ledger by business date: what was posted to folios, what was
// received, what was billed, and what guests still owe — opening + posted −
// received − billed = closing. Dated by the posting date, so a day closed by
// night audit never changes (corrections show on the day they are made).
// Restaurant bills sit in their own column: owed on the folio, invoiced by
// DineOps. Billed fills from slice 7 (checkout invoices). Checks under the
// header: the ledger equals the folio balances, posted tax equals its frozen
// components, and closed nights corrected after their audit. A date opens its
// lines.

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { EnterpriseScreenShell } from 'components/enterprise-shell';
import ReportHeader, { ReportBody, type ReportHeaderItem } from 'components/reports/ReportHeader';
import type { ActionButton } from 'components/modals';
import { SyutGrid, type SyutGridColumn, SyutSelect, type SelectOption } from 'components/ui';
import { reportService } from 'api/services/reportService';
import { propertyService } from 'api/services/propertyService';
import notificationService from 'services/notificationService';
import { blobErrorMessage, reserveTab, saveReportFile, showPdf, type ReportFormat } from 'utils/reportFile';
import { formatCurrency } from 'utils/currencyFormat';
import { useDefaultProperty } from 'hooks/api/useDefaultProperty';
import { usePropertyLabel } from 'hooks/api/usePropertyLabel';
import type { GuestLedgerDay, GuestLedgerLine, GuestLedgerReport, ReportFilter } from 'api/types';
import 'components/modules/dashboard-module/dashboard.css';

const isoOf = (d: Date) => new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 10);
const todayIso = () => isoOf(new Date());
const weekAgoIso = () => { const d = new Date(); d.setDate(d.getDate() - 6); return isoOf(d); };
const formatDate = (iso?: string | null) =>
  iso ? new Date(iso).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '-';

const SOURCE: Record<string, string> = {
  NIGHT_AUDIT: 'Night audit', FRONT_OFFICE: 'Front Office', DINEOPS: 'Restaurant', CRS: 'Other (earlier lines)',
};

const money = (v: number | null | undefined, muted = false) => (
  <span style={{ color: v != null && v < 0 ? 'var(--danger, #dc2626)' : muted && !v ? 'var(--text-muted)' : undefined }}>
    {v == null ? '—' : formatCurrency(v)}
  </span>
);

interface Props {
  moduleIcon?: string;
  tabId?: string;
  moduleName?: string;
  /** RBAC — {'view','export'}. Fail-closed: [] renders no export/print. */
  allowedActions?: string[];
}

type DayRow = GuestLedgerDay & { id: string };
type LineRow = GuestLedgerLine & { id: string };

const GuestLedgerReportScreen: React.FC<Props> = ({ tabId, allowedActions = [] }) => {
  const [propertyId, setPropertyId] = useState('');
  const { defaultPropertyId } = useDefaultProperty();
  useEffect(() => { if (!propertyId && defaultPropertyId) setPropertyId(defaultPropertyId); }, [defaultPropertyId, propertyId]);
  const [from, setFrom] = useState(weekAgoIso());
  const [to, setTo] = useState(todayIso());
  const [filterOpen, setFilterOpen] = useState(false);
  const propertyLabelsRef = useRef<Record<string, string>>({});
  const fetchedPropertyLabel = usePropertyLabel(propertyId);
  const propertyLabel = fetchedPropertyLabel || propertyLabelsRef.current[propertyId] || '';

  const enabled = !!propertyId && !!from && !!to && from <= to;
  const filter: ReportFilter = useMemo(() => ({ propertyId, from, to }), [propertyId, from, to]);

  const [report, setReport] = useState<GuestLedgerReport | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const load = useCallback(async () => {
    if (!enabled) { setReport(null); return; }
    setLoading(true);
    setError(null);
    try {
      setReport(await reportService.getGuestLedger(filter));
    } catch (err: any) {
      setError(err?.response?.data?.message || err?.message || 'The guest ledger did not load.');
    } finally {
      setLoading(false);
    }
  }, [enabled, filter]);
  useEffect(() => { setDrillDate(null); void load(); }, [load]);

  // ── drill: one date's lines ──
  const [drillDate, setDrillDate] = useState<string | null>(null);
  const [lines, setLines] = useState<LineRow[]>([]);
  const [linesLoading, setLinesLoading] = useState(false);
  const openDay = useCallback(async (date: string) => {
    setDrillDate(date);
    setLinesLoading(true);
    try {
      const rows = await reportService.getGuestLedgerLines(propertyId, date.slice(0, 10));
      setLines(rows.map((l, i) => ({ ...l, id: `${l.kind}-${i}` })));
    } catch (err: any) {
      notificationService.toast.error(err?.response?.data?.message || err?.message || 'The lines did not load.');
    } finally {
      setLinesLoading(false);
    }
  }, [propertyId]);

  const loadProperties = useCallback(async (q: string): Promise<SelectOption[]> => {
    const res = await propertyService.getAll({ searchTerm: q || undefined, pageSize: q ? 50 : 500, includeInactive: false });
    const opts = (res.data ?? []).map((p: any) => ({ value: p.id, label: `${p.propertyCode} - ${p.propertyName}` }));
    opts.forEach((o: SelectOption) => { propertyLabelsRef.current[o.value as string] = o.label; });
    return opts;
  }, []);

  // Export / Print — built by the API (the Fairbooks way). Drilled into a date, they
  // export that date's lines, as on the screen; otherwise the daily ledger.
  const [exporting, setExporting] = useState(false);
  const [printing, setPrinting] = useState(false);
  const exportFile = useCallback(async (format: ReportFormat) => {
    if (!enabled) { notificationService.toast.warning('Select a property and a valid period first.'); return; }
    const tab = format === 'pdf' ? reserveTab() : null;
    (format === 'pdf' ? setPrinting : setExporting)(true);
    try {
      const day = drillDate?.slice(0, 10);
      const { blob, disposition } = day
        ? await reportService.exportGuestLedgerLines(propertyId, day, format)
        : await reportService.exportGuestLedger(filter, format);
      if (format === 'pdf') showPdf(blob, tab);
      else saveReportFile(blob, disposition, day ? `GuestLedger_${day}_lines.xlsx` : `GuestLedger_${from}_${to}.xlsx`);
    } catch (err) {
      tab?.close();
      notificationService.toast.error(await blobErrorMessage(err, format === 'pdf' ? 'Print failed.' : 'Export failed.'));
    } finally {
      (format === 'pdf' ? setPrinting : setExporting)(false);
    }
  }, [enabled, filter, from, to, drillDate, propertyId]);

  const dayRows = useMemo<DayRow[]>(() => (report?.days ?? []).map((d) => ({ ...d, id: d.date })), [report]);

  const dayColumns = useMemo<SyutGridColumn<DayRow>[]>(() => [
    {
      id: 'date', header: 'Business date', accessorKey: 'date', size: 150,
      cell: ({ row }) => (
        <span>
          <button type="button" title="Open the day's lines" onClick={() => { void openDay(row.original.date); }}
            style={{ background: 'none', border: 'none', padding: 0, color: 'var(--primary)', cursor: 'pointer', fontWeight: 600 }}>
            {formatDate(row.original.date)}
          </button>
          {row.original.isClosed && <span className="badge badge-secondary" style={{ fontSize: '0.6rem', marginLeft: '0.35rem' }} title="Closed by night audit">closed</span>}
        </span>
      ),
    },
    { id: 'opening', header: 'Opening', accessorKey: 'opening', size: 130, cell: ({ row }) => money(row.original.opening) },
    { id: 'postedNet', header: 'Posted net', accessorKey: 'postedNet', size: 120, cell: ({ row }) => money(row.original.postedNet, true) },
    { id: 'postedTax', header: 'Tax', accessorKey: 'postedTax', size: 100, cell: ({ row }) => money(row.original.postedTax, true) },
    { id: 'postedElsewhere', header: 'Invoiced elsewhere', accessorKey: 'postedElsewhere', size: 140, cell: ({ row }) => money(row.original.postedElsewhere, true) },
    { id: 'received', header: 'Received', accessorKey: 'received', size: 120, cell: ({ row }) => money(row.original.received, true) },
    { id: 'billed', header: 'Billed', accessorKey: 'billed', size: 100, cell: ({ row }) => money(row.original.billed) },
    { id: 'closing', header: 'Closing', accessorKey: 'closing', size: 130, cell: ({ row }) => <b>{money(row.original.closing)}</b> },
  ], [openDay]);

  const lineColumns = useMemo<SyutGridColumn<LineRow>[]>(() => [
    {
      id: 'kind', header: 'Kind', accessorKey: 'kind', size: 100,
      cell: ({ row }) => (
        <span className={`badge ${row.original.kind === 'CHARGE' ? 'badge-info' : row.original.kind === 'VOID' ? 'badge-warning' : 'badge-success'}`}
          style={{ fontSize: '0.62rem' }}>
          {row.original.kind === 'CHARGE' ? 'Posted' : row.original.kind === 'VOID' ? 'Receipt void' : 'Received'}
        </span>
      ),
    },
    { id: 'folioNumber', header: 'Folio', accessorKey: 'folioNumber', size: 140, cell: ({ row }) => <span style={{ fontFamily: 'monospace' }}>{row.original.folioNumber || '—'}</span> },
    {
      id: 'guestName', header: 'Guest / Booking', accessorKey: 'guestName', size: 190,
      cell: ({ row }) => (
        <span>{row.original.guestName || '—'}
          {row.original.bookingNumber && <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)' }}>{row.original.bookingNumber}</div>}
        </span>
      ),
    },
    { id: 'chargeType', header: 'Type', accessorKey: 'chargeType', size: 100, cell: ({ row }) => row.original.chargeType || '—' },
    {
      id: 'description', header: 'Description', accessorKey: 'description', size: 320,
      cell: ({ row }) => (
        <span style={{ whiteSpace: 'normal', fontSize: '0.8rem' }}>
          {row.original.description}
          {row.original.billingState === 'INVOICED_ELSEWHERE' && <span className="badge badge-secondary" style={{ fontSize: '0.58rem', marginLeft: '0.3rem' }}>invoiced elsewhere</span>}
        </span>
      ),
    },
    { id: 'source', header: 'Source', accessorKey: 'source', size: 120, cell: ({ row }) => SOURCE[row.original.source ?? ''] ?? row.original.source ?? '—' },
    { id: 'posted', header: 'Posted', accessorKey: 'posted', size: 120, cell: ({ row }) => money(row.original.posted, true) },
    { id: 'received', header: 'Received', accessorKey: 'received', size: 120, cell: ({ row }) => money(row.original.received, true) },
  ], []);

  const actionButtons: ActionButton[] = useMemo(() => {
    const buttons: ActionButton[] = [
      { id: 'refresh', icon: <IconSystem.refresh size={16} />, label: 'Refresh', onClick: () => { void load(); if (drillDate) void openDay(drillDate); }, variant: 'icon' },
    ];
    if (allowedActions.includes('export')) {
      buttons.push({ id: 'export', icon: <IconSystem.download size={16} />, label: exporting ? 'Exporting…' : 'Export', tooltip: drillDate ? `Export ${formatDate(drillDate)} lines to Excel` : 'Export to Excel', onClick: () => { void exportFile('xlsx'); }, variant: 'icon' });
      buttons.push({ id: 'print', icon: <IconSystem.print size={16} />, label: printing ? 'Preparing…' : 'Print', tooltip: drillDate ? `Print ${formatDate(drillDate)} lines` : 'Print the guest ledger', onClick: () => { void exportFile('pdf'); }, variant: 'icon' });
    }
    return buttons;
  }, [load, drillDate, openDay, allowedActions, exporting, printing, exportFile]);

  const periodLabel = from === to ? formatDate(from) : `${formatDate(from)} – ${formatDate(to)}`;
  const check = (ok: boolean, okText: string, badText: string, title: string) => ({
    label: ok ? 'Check' : 'Check failed', value: <span style={{ color: ok ? 'var(--success, #16a34a)' : 'var(--danger, #dc2626)' }}>{ok ? okText : badText}</span>,
    icon: (ok ? 'checkCircle' : 'alert') as 'checkCircle' | 'alert', title,
  });
  const headerItems: ReportHeaderItem[] = [
    { label: 'Property', value: propertyLabel || (propertyId ? '…' : 'Select in filters'), primary: true },
    { label: 'Period', value: periodLabel, primary: true, title: 'Business dates — lines dated by their posting date' },
    ...(report ? [
      { label: 'Opening', value: formatCurrency(report.opening) },
      { label: 'Posted', value: formatCurrency(report.postedTotal), title: 'Charges the hotel invoices, net + tax' },
      { label: 'Invoiced elsewhere', value: formatCurrency(report.postedElsewhere), title: 'Restaurant bills owed on folios' },
      { label: 'Received', value: formatCurrency(report.received) },
      { label: 'Billed', value: report.billed != null ? formatCurrency(report.billed) : 'from slice 7', title: 'Invoices drawn at checkout' },
      { label: 'Closing', value: formatCurrency(report.closing), title: 'Still owed by guests on folios (negative = credit held)' },
      check(report.ledgerDifference === 0, 'Ledger = folios', `Ledger ≠ folios by ${formatCurrency(report.ledgerDifference)}`,
        `Ledger now ${formatCurrency(report.ledgerNow)} · folio balances ${formatCurrency(report.folioBalancesNow)}`),
      check(report.taxDifference === 0, 'Tax = components', `Tax ≠ components by ${formatCurrency(report.taxDifference)}`,
        `Posted tax ${formatCurrency(report.taxOnComponentLines)} · components ${formatCurrency(report.componentsTotal)}`),
      ...(report.auditDifferences.length > 0 ? [{
        label: 'Corrected nights', value: String(report.auditDifferences.length), icon: 'info' as const,
        title: report.auditDifferences.map((a) => `${formatDate(a.date)}: audit ${formatCurrency(a.auditRoomRevenue)}, now ${formatCurrency(a.postedRoomRevenue)}`).join(' · '),
      }] : []),
    ] : []),
  ];

  const footer = drillDate ? (
    <>
      <button type="button" className="btn btn-secondary btn-sm" onClick={() => setDrillDate(null)}>
        <IconSystem.arrowLeft size={14} /> All dates
      </button>
      <b>{formatDate(drillDate)}</b>
      <span>Posted {formatCurrency(lines.reduce((s, l) => s + l.posted, 0))}</span>
      <span>Received {formatCurrency(lines.reduce((s, l) => s + l.received, 0))}</span>
    </>
  ) : report && (report.byChargeType.length > 0 || report.bySource.length > 0) ? (
    <>
      <b>By type</b>
      {report.byChargeType.map((t) => <span key={t.key}>{t.name || t.key} <b>{formatCurrency(t.total)}</b></span>)}
      <b style={{ marginLeft: '0.75rem' }}>By source</b>
      {report.bySource.map((s) => <span key={s.key}>{SOURCE[s.key] ?? s.key} <b>{formatCurrency(s.total)}</b></span>)}
    </>
  ) : undefined;

  return (
    <EnterpriseScreenShell
      title="Guest Ledger"
      description="Posted vs Billed by business date — what guests owe on their folios, and how it moved."
      icon={<IconSystem.list size={18} />}
      tabId={tabId}
      actionButtons={actionButtons}
      filterConfig={{ onClick: () => setFilterOpen((v) => !v), isActive: filterOpen, appliedCount: (from !== weekAgoIso() || to !== todayIso()) ? 1 : 0 }}
      trailingContent={
        filterOpen && (
          <div className="search-panel">
            <div className="search-panel-header">
              <h3 className="search-panel-title">Filters</h3>
              <button className="search-panel-close" onClick={() => setFilterOpen(false)} title="Close filters"><IconSystem.x size={14} /></button>
            </div>
            <div className="search-panel-content">
              <div className="filter-section">
                <SyutSelect
                  label={<>Property <span className="text-danger">*</span></>}
                  value={propertyId}
                  onChange={(v) => setPropertyId((v as string) || '')}
                  isAsync
                  loadOptions={loadProperties}
                  defaultOptions={true}
                  cacheOptions={false}
                  isClearable={false}
                  menuPortalTarget={document.body}
                  size="sm"
                />
              </div>
              <div className="filter-section">
                <label className="form-label" style={{ fontSize: '0.8125rem', marginBottom: '0.25rem' }}>From <span className="text-danger">*</span></label>
                <input type="date" className="form-input" value={from} onChange={(e) => setFrom(e.target.value)} style={{ fontSize: '0.8125rem', padding: '0.375rem 0.625rem' }} />
              </div>
              <div className="filter-section">
                <label className="form-label" style={{ fontSize: '0.8125rem', marginBottom: '0.25rem' }}>To <span className="text-danger">*</span></label>
                <input type="date" className="form-input" value={to} min={from} onChange={(e) => setTo(e.target.value)} style={{ fontSize: '0.8125rem', padding: '0.375rem 0.625rem' }} />
                <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', marginTop: '0.15rem' }}>Business dates, at most a year.</div>
              </div>
            </div>
            <div className="filter-actions">
              <button className="btn btn-secondary" onClick={() => { setFrom(weekAgoIso()); setTo(todayIso()); }}>Clear</button>
              <button className="btn btn-primary" onClick={() => setFilterOpen(false)}>Apply</button>
            </div>
          </div>
        )
      }
    >
      <ReportHeader items={headerItems} />
      <ReportBody footer={footer}>
        {!enabled ? (
          <div style={{ color: 'var(--text-muted)', padding: '2rem', textAlign: 'center' }}>Pick a property and a period in the filters.</div>
        ) : error ? (
          <div style={{ color: 'var(--danger, #ef4444)', padding: '2rem', textAlign: 'center' }}>Failed to load — {error}</div>
        ) : drillDate ? (
          <SyutGrid data={lines} columns={lineColumns} isLoading={linesLoading} emptyMessage="Nothing moved on this date." />
        ) : (
          <SyutGrid data={dayRows} columns={dayColumns} isLoading={loading} emptyMessage="No dates in the period." />
        )}
      </ReportBody>
    </EnterpriseScreenShell>
  );
};

export default GuestLedgerReportScreen;
